'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
    ArrowLeft,
    HandCoins,
    Save,
    Calendar,
    FileText,
    Banknote,
    User,
    CheckCircle2
} from 'lucide-react';
import Link from 'next/link';

export default function AddSetoranMasukPage() {
    const router = useRouter();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        description: '',
        category: 'SPP Siswa',
        amount: '',
        receivedBy: 'Admin Keuangan',
        paymentMethod: 'Transfer BSI'
    });

    const categories = ['SPP Siswa', 'Uang Pangkal', 'Donasi', 'Seragam & Buku', 'Lainnya'];
    const paymentMethods = ['Tunai', 'Transfer BSI', 'Transfer Mandiri', 'Transfer BRK', 'QRIS'];

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate API call
        setTimeout(() => {
            setIsSubmitting(false);
            setIsSuccess(true);
            setTimeout(() => {
                router.push('/sekolah/setoran-masuk');
            }, 1500);
        }, 1000);
    };

    if (isSuccess) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center animate-in fade-in zoom-in duration-500">
                <div className="w-24 h-24 bg-success-soft rounded-full flex items-center justify-center mb-6 shadow-xl shadow-success/20">
                    <CheckCircle2 className="w-12 h-12 text-success" />
                </div>
                <h2 className="text-3xl font-black text-foreground mb-2">Berhasil Disimpan!</h2>
                <p className="text-muted font-medium mb-8">Data setoran masuk telah berhasil dicatat dalam sistem.</p>
                <div className="animate-pulse flex gap-2 items-center text-sm font-bold text-primary">
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                    Redirecting...
                </div>
            </div>
        );
    }

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-5xl mx-auto w-full pb-32 animate-in slide-in-from-right duration-500">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                        <Link href="/sekolah/setoran-masuk" className="hover:text-primary transition-all">Setoran Masuk</Link>
                        <span className="text-muted/50">•</span>
                        <span className="text-foreground">Input Baru</span>
                    </div>
                    <h1 className="text-3xl font-black text-foreground tracking-tight">Catat Setoran Baru</h1>
                </div>
                <Link
                    href="/sekolah/setoran-masuk"
                    className="group flex items-center gap-2 text-secondary hover:text-primary transition-all bg-surface px-4 py-2 rounded-xl border border-border-main shadow-sm"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Kembali</span>
                </Link>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-surface border border-border-main rounded-3xl p-8 space-y-6 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Date */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-muted uppercase tracking-wider">Tanggal Transaksi</label>
                                <div className="relative">
                                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                                    <input
                                        type="date"
                                        required
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
                                    />
                                </div>
                            </div>

                            {/* Category */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-muted uppercase tracking-wider">Kategori Penerimaan</label>
                                <div className="relative">
                                    <HandCoins className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none appearance-none cursor-pointer"
                                    >
                                        {categories.map((cat) => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-muted uppercase tracking-wider">Keterangan / Uraian</label>
                            <div className="relative">
                                <FileText className="absolute left-4 top-3.5 w-4 h-4 text-muted" />
                                <textarea
                                    required
                                    rows={3}
                                    placeholder="Contoh: Pembayaran SPP bulan Januari 2024 a.n. Budi Santoso"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none resize-none"
                                />
                            </div>
                        </div>

                        {/* Amount */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-muted uppercase tracking-wider">Nominal (Rp)</label>
                            <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xs font-black text-muted">Rp</span>
                                <input
                                    type="number"
                                    required
                                    min="0"
                                    placeholder="0"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    className="w-full pl-10 pr-4 py-3 bg-background border border-border-main rounded-xl text-lg font-black tracking-tight focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar Details */}
                <div className="space-y-6">
                    <div className="bg-surface border border-border-main rounded-3xl p-6 space-y-6 shadow-sm">
                        <h3 className="text-sm font-black text-foreground border-b border-border-main pb-4">Detail Penerimaan</h3>

                        {/* Payment Method */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-muted uppercase tracking-wider">Metode Pembayaran</label>
                            <div className="relative">
                                <Banknote className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                                <select
                                    value={formData.paymentMethod}
                                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                                    className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-xl text-sm font-semibold focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none appearance-none cursor-pointer"
                                >
                                    {paymentMethods.map((method) => (
                                        <option key={method} value={method}>{method}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Received By */}
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-muted uppercase tracking-wider">Diterima Oleh</label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                                <input
                                    type="text"
                                    readOnly
                                    value={formData.receivedBy}
                                    className="w-full pl-11 pr-4 py-3 bg-background/50 border border-border-main rounded-xl text-sm font-bold text-muted cursor-not-allowed outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-primary text-white rounded-2xl text-sm font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:bg-primary/90 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all"
                    >
                        {isSubmitting ? (
                            <span className="animate-pulse">Menyimpan...</span>
                        ) : (
                            <>
                                <Save className="w-5 h-5" /> Simpan Data
                            </>
                        )}
                    </button>
                </div>
            </form>
        </main>
    );
}
